import { aircraftType } from '../data/aircraft';
import { airportById } from '../data/airports';
import { unlockAssistants, weeklyAssistantSalaries } from '../assistants';
import { emptyRouteStats } from '../game/createGame';
import { processFuelStorageQueue, sellOneFuelStorageFacility, settleFuelWeek } from '../game/fuel';
import { advanceListingApplication, delistCompany, updateSharePrice } from '../game/listing';
import { updateObjectives } from '../game/objectives';
import { decrementEffects, sellOneAircraft } from '../game/quizFlow';
import { advanceAiManagersForWeek } from '../game/aiManagers';
import { finalizeVentureRounds, processAutomaticDividends } from '../game/capitalMarkets';
import {
  averageCrewMood,
  createCrewResignationEvent,
  crewSeverance,
  isCrewAngry,
  routeReputationDelta,
  routeReputationMultiplier,
  updateAircraftCrewMood,
} from './crew';
import type { AircraftAsset, CabinClass, GameEvent, GameState, HubAsset, LedgerEntry, MarketSignal, Route, WeeklyReport } from '../game/types';
import { applyDifficultyMilestone, autoMaintainLowConditionAircraft } from '../game/difficulty';
import { bankLoanAnnualRate, bankLoanAverageWeeklyInterest, bankLoanCapacity, bankLoanMonthlyInterest, BANK_LOAN_INTEREST_INTERVAL_WEEKS, BANK_LOAN_TERM_WEEKS } from './credit';
import { aircraftFareMultiplier, aircraftFareRatio, aircraftOperation, aircraftServiceScore, baseFare, CABIN_CLASSES, fuelPriceIndex, hubFeeDiscountFactor, hubSpecializationEffects, marketDemand, maturityFactor, routeCabinProfile, routeCabinDemandMultiplier, ROUTE_ADMIN_COST, type AircraftOperation, routeFareMultiplier, routeMarketShare } from './economy';
import { airportCostMultiplier, applyAircraftMarketEventCosts, createWeeklyMarketEvent, decrementMarketEvents, fuelCostMultiplier, mergeMarketEvents, routeDemandMultiplier, routeRevenueMultiplier, shouldCreateMarketEvent } from './marketEvents';
import { clamp, distanceKm, hashFloat } from './math';

interface AircraftWork {
  asset: AircraftAsset;
  operation: AircraftOperation;
  score: number;
  remainingSeatsByCabin: Record<CabinClass, number>;
}

interface WorkingRoute {
  route: Route;
  distance: number;
  remainingSeatsByCabin: Record<CabinClass, number>;
  flightCost: number;
  fuelGallons: number;
  ownershipCost: number;
  cycleCost: number;
  landingCost: number;
  totalFlightHours: number;
  seatsByCabin: Record<CabinClass, number>;
  aircraftWork: AircraftWork[];
}

const clone = <T,>(value: T): T => structuredClone(value);
const pairKey = (a: string, b: string) => [a, b].sort().join('-');

function applyPendingFareRatios(state: GameState): void {
  for (const route of state.routes) {
    if (route.pendingFareRatio === undefined) continue;
    route.fareRatio = route.pendingFareRatio;
    route.pendingFareRatio = undefined;
  }
  for (const aircraft of state.aircraft) {
    if (aircraft.pendingFareRatio === undefined) continue;
    aircraft.fareRatio = aircraft.pendingFareRatio;
    aircraft.pendingFareRatio = undefined;
  }
}

function hubForRoute(state: GameState, route: Route): HubAsset | undefined {
  return state.hubs.find((hub) => hub.airportId === route.originId)
    ?? state.hubs.find((hub) => hub.airportId === route.destinationId);
}

function hubRouteMultiplier(hub?: HubAsset) {
  if (!hub) return { demand: 1, revenue: 1, airportCost: 1, passengerCost: 1, transfer: 1 };
  const effects = hubSpecializationEffects(hub.specialization);
  const levelBonus = 1 + (hub.level - 1) * 0.035;
  return {
    demand: effects.demand * levelBonus,
    revenue: effects.revenue * (1 + (hub.level - 1) * 0.018),
    airportCost: effects.airportCost,
    passengerCost: effects.passengerCost,
    transfer: effects.transfer * (1 + (hub.level - 1) * 0.22),
  };
}

function aircraftPassengerScore(asset: AircraftAsset, state: GameState, route: Route): number {
  return aircraftServiceScore(asset, state.reputation) * routeReputationMultiplier(route.reputationScore ?? 25);
}

function aircraftPassengerAttractiveness(work: AircraftWork, route: Route): number {
  return work.score * (100 / Math.max(1, aircraftFareRatio(work.asset, route)));
}

function addLedger(next: GameState, entry: Omit<LedgerEntry, 'id' | 'week'>): void {
  next.ledger.push({ ...entry, id: `ledger-${next.nextId++}`, week: next.week });
}

function buildSignals(report: Omit<WeeklyReport, 'signals'>, previous?: WeeklyReport): MarketSignal[] {
  const signals: MarketSignal[] = [];
  if (report.fuelPriceIndex > 1.12) signals.push({ id: 'fuel-high', tone: 'negative', title: '燃油价格处于高位', detail: '短程高频航线受到的成本压力最明显。' });
  if (report.fuelPriceIndex < 0.9) signals.push({ id: 'fuel-low', tone: 'positive', title: '燃油成本窗口', detail: '长途航线和低价策略的利润空间正在改善。' });
  if (report.averageLoadFactor > 0.94) signals.push({ id: 'capacity-tight', tone: 'positive', title: '全网运力接近上限', detail: '满载率不会超过100%，多余需求已计入未满足需求。' });
  if (report.unmetDemand > report.uniquePassengers * 0.22) signals.push({ id: 'unmet', tone: 'neutral', title: '存在明显未满足需求', detail: '可考虑增派飞机，但应先核对新增投资的回本周期。' });
  if (previous && report.operatingProfit < previous.operatingProfit * 0.78) signals.push({ id: 'profit-drop', tone: 'negative', title: '经营利润显著下降', detail: '请检查燃油、载客率和枢纽固定成本。' });
  if (!signals.length) signals.push({ id: 'stable', tone: 'neutral', title: '市场运行平稳', detail: '这是复盘低效资产和规划下一笔投资的合适时机。' });
  return signals.slice(0, 3);
}

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function isAircraftFailure(state: GameState, asset: AircraftAsset): boolean {
  if (asset.condition >= 0.7) return false;
  const roll = hashFloat(state.seed, state.week, hashString(asset.id), 0xfa11);
  if (asset.condition < 0.5) return roll < 0.02;
  return roll < 0.01;
}

function createFailureEvent(state: GameState, asset: AircraftAsset, cost: number): GameEvent {
  const type = aircraftType(asset.typeId);
  const severity = asset.condition < 0.5 ? '严重' : '一般';
  return {
    id: `event-${state.week}-failure-${asset.id}`,
    week: state.week,
    tone: 'negative',
    title: `${type.name} 机械故障（${severity}）`,
    detail: `维护率 ${(asset.condition * 100).toFixed(0)}% 的 ${type.name} 本周发生故障并停场维修，固定成本仍需支付且没有收入。请及时保养机队以避免连续故障。`,
    impact: {
      aircraftId: asset.id,
      aircraftName: type.name,
      cost,
      lostRevenue: 0,
    },
  };
}

export function simulateWeek(state: GameState): GameState {
  if (state.gameOver) return state;
  let next = clone(state);
  next.week += 1;
  next = applyDifficultyMilestone(next);
  next = autoMaintainLowConditionAircraft(next);
  next = processFuelStorageQueue(next);
  applyPendingFareRatios(next);
  next = finalizeVentureRounds(next);
  next = advanceAiManagersForWeek(next);
  next.activeMarketEvents ??= [];

  const failureEvents: GameEvent[] = [];

  // 机组情绪与口碑（执飞飞机会因薪资产生情绪变化；休息飞机高薪也会回升心情）
  const groundedAircraftIds = new Set<string>();
  for (const asset of next.aircraft) {
    asset.crewMood ??= 100;
    asset.crewHiredWeek ??= asset.acquiredWeek;
    if (asset.routeIds.length) {
      updateAircraftCrewMood(asset);
    } else if (asset.crewSalaryModifier > 1.2) {
      // 休息航班员工工资高于基准 20%，按超出比例恢复心情
      asset.crewMood += (asset.crewSalaryModifier - 1.2) * 20;
      asset.crewMood = Math.max(0, Math.min(100, asset.crewMood));
    }
    if (isCrewAngry(asset) && asset.routeIds.length) {
      groundedAircraftIds.add(asset.id);
      const severance = crewSeverance(asset, next.week);
      next.cash -= severance;
      failureEvents.push(createCrewResignationEvent(next, asset, severance));
      addLedger(next, {
        category: 'operating_expense',
        cashAmount: -severance,
        profitAmount: -severance,
        objectId: asset.id,
        label: `${aircraftType(asset.typeId).name} 机组${asset.lease ? '罢工赔偿' : '离职赔偿'}`,
      });
    }
  }

  const fuelEffectMultiplier = next.activeEffects.reduce((multiplier, effect) => (
    effect.kind === 'fuelCostModifier' ? multiplier * effect.multiplier : multiplier
  ), 1);
  const fuelIndex = fuelPriceIndex(next.week, next.seed) * fuelCostMultiplier(next) * fuelEffectMultiplier;
  const working = new Map<string, WorkingRoute>();
  const requestedMovements = new Map<string, number>();
  const brokenAircraftIds = new Set<string>();

  for (const asset of next.aircraft) {
    if (isAircraftFailure(next, asset)) {
      brokenAircraftIds.add(asset.id);
    }
  }

  for (const route of next.routes.filter((item) => item.active)) {
    route.reputationScore ??= 25;
    const origin = airportById(route.originId);
    const destination = airportById(route.destinationId);
    const distance = distanceKm(origin, destination);
    const operations = route.aircraftIds
      .map((aircraftId) => next.aircraft.find((item) => item.id === aircraftId))
      .filter((asset) => asset !== undefined)
      .map((asset) => {
        const operation = applyAircraftMarketEventCosts(asset, aircraftOperation(asset, distance, fuelIndex), next);
        if (brokenAircraftIds.has(asset.id) || groundedAircraftIds.has(asset.id)) {
          operation.seats = 0;
          operation.roundTrips = 0;
          operation.flightHours = 0;
          operation.flightCost = 0;
          operation.cycleCost = 0;
          operation.cabins = { economy: 0, business: 0, first: 0 };
          if (brokenAircraftIds.has(asset.id)) {
            failureEvents.push(createFailureEvent(next, asset, operation.ownershipCost));
          }
        }
        const score = aircraftPassengerScore(asset, next, route);
        return { asset, operation, score };
      })
      .sort((a, b) => (b.score * (100 / Math.max(1, aircraftFareRatio(b.asset, route)))) - (a.score * (100 / Math.max(1, aircraftFareRatio(a.asset, route)))));
    const roundTrips = operations.reduce((sum, item) => sum + item.operation.roundTrips, 0);
    const seatsByCabin: Record<CabinClass, number> = { economy: 0, business: 0, first: 0 };
    for (const item of operations) {
      for (const cabin of CABIN_CLASSES) {
        seatsByCabin[cabin] += item.operation.cabins[cabin];
      }
    }
    const aircraftWork: AircraftWork[] = operations.map(({ asset, operation, score }) => ({
      asset,
      operation,
      score,
      remainingSeatsByCabin: { ...operation.cabins },
    }));
    requestedMovements.set(origin.iata, (requestedMovements.get(origin.iata) ?? 0) + roundTrips * 2);
    requestedMovements.set(destination.iata, (requestedMovements.get(destination.iata) ?? 0) + roundTrips * 2);
    route.stats = emptyRouteStats(distance);
    for (const cabin of CABIN_CLASSES) {
      route.stats.cabins[cabin].seats = seatsByCabin[cabin];
    }
    working.set(route.id, {
      route,
      distance,
      seatsByCabin,
      remainingSeatsByCabin: { ...seatsByCabin },
      flightCost: operations.reduce((sum, item) => sum + item.operation.flightCost, 0),
      fuelGallons: operations.reduce((sum, item) => sum + item.operation.fuelGallons, 0),
      ownershipCost: operations.reduce((sum, item) => sum + item.operation.ownershipCost, 0),
      cycleCost: operations.reduce((sum, item) => sum + item.operation.cycleCost, 0),
      landingCost: 0,
      totalFlightHours: operations.reduce((sum, item) => sum + item.operation.flightHours, 0),
      aircraftWork,
    });
    route.stats.roundTrips = roundTrips;
    route.stats.seats = operations.reduce((sum, item) => sum + item.operation.seats, 0);
  }

  // 机场时刻是硬容量。拥堵时所有相关航线按比例缩减班次和座位。
  for (const item of working.values()) {
    const origin = airportById(item.route.originId);
    const destination = airportById(item.route.destinationId);
    const originFactor = Math.min(1, origin.weeklyMovements / Math.max(1, requestedMovements.get(origin.iata) ?? 1));
    const destinationFactor = Math.min(1, destination.weeklyMovements / Math.max(1, requestedMovements.get(destination.iata) ?? 1));
    const factor = Math.min(originFactor, destinationFactor);
    if (factor < 1) {
      item.route.stats.roundTrips = Math.max(1, Math.floor(item.route.stats.roundTrips * factor));
      item.route.stats.seats = Math.max(0, Math.floor(item.route.stats.seats * factor));
      item.flightCost *= factor;
      item.fuelGallons *= factor;
      item.cycleCost *= factor;
      item.totalFlightHours *= factor;
      for (const cabin of CABIN_CLASSES) {
        item.seatsByCabin[cabin] = Math.floor(item.seatsByCabin[cabin] * factor);
        item.route.stats.cabins[cabin].seats = item.seatsByCabin[cabin];
      }
      for (const work of item.aircraftWork) {
        for (const cabin of CABIN_CLASSES) {
          work.operation.cabins[cabin] = Math.floor(work.operation.cabins[cabin] * factor);
        }
      }
    }
    for (const work of item.aircraftWork) {
      work.remainingSeatsByCabin = { ...work.operation.cabins };
    }
    item.remainingSeatsByCabin = { ...item.seatsByCabin };
    const hubMultiplier = hubRouteMultiplier(hubForRoute(next, item.route));
    item.landingCost = item.route.stats.roundTrips * (800 + (origin.feeIndex + destination.feeIndex) * 900) * airportCostMultiplier(origin.iata, destination.iata, next) * hubMultiplier.airportCost;
  }

  // 根据执飞机组的平均情绪更新航线口碑
  for (const item of working.values()) {
    const avgMood = averageCrewMood(item.route.aircraftIds, next);
    item.route.reputationScore = Math.max(0, Math.min(100, item.route.reputationScore + routeReputationDelta(avgMood)));
  }

  let uniquePassengers = 0;
  let connectionPassengers = 0;

  // 先满足直达旅客。每个航段的本地客流只由城市市场生成，不随飞机大小变化。
  for (const item of working.values()) {
    const origin = airportById(item.route.originId);
    const destination = airportById(item.route.destinationId);
    const hubMultiplier = hubRouteMultiplier(hubForRoute(next, item.route));
    const baseDemand = marketDemand(origin, destination, next) * routeMarketShare(item.route, next) * maturityFactor(item.route.maturityWeeks, item.route.openedWeek) * routeDemandMultiplier(item.route, origin, destination, next) * hubMultiplier.demand;
    const routeRevenueFactor = routeRevenueMultiplier(item.route, origin, destination, next) * hubMultiplier.revenue;
    const profile = routeCabinProfile(origin, destination);
    for (const cabin of CABIN_CLASSES) {
      let remainingDemand = Math.round(baseDemand * profile[cabin] * routeCabinDemandMultiplier(item.route, cabin, next));
      let passengers = 0;
      let cabinRevenue = 0;
      const aircraftWork = [...item.aircraftWork].sort((a, b) => aircraftPassengerAttractiveness(b, item.route) - aircraftPassengerAttractiveness(a, item.route));
      for (const work of aircraftWork) {
        if (remainingDemand <= 0) break;
        const allocated = Math.min(remainingDemand, work.remainingSeatsByCabin[cabin]);
        work.remainingSeatsByCabin[cabin] -= allocated;
        remainingDemand -= allocated;
        passengers += allocated;
        cabinRevenue += allocated * baseFare(item.distance, cabin) * routeRevenueFactor * aircraftFareMultiplier(work.asset, item.route);
      }
      item.remainingSeatsByCabin[cabin] -= passengers;
      cabinRevenue = Math.round(cabinRevenue);
      const cabinStats = item.route.stats.cabins[cabin];
      cabinStats.demand += remainingDemand + passengers;
      cabinStats.passengers += passengers;
      cabinStats.revenue += cabinRevenue;
      cabinStats.unmetDemand = Math.max(0, cabinStats.demand - cabinStats.passengers);
      cabinStats.loadFactor = cabinStats.seats > 0 ? Math.min(1, cabinStats.passengers / cabinStats.seats) : 0;

      item.route.stats.demand += remainingDemand + passengers;
      item.route.stats.localPassengers += passengers;
      item.route.stats.passengers += passengers;
      item.route.stats.unmetDemand += Math.max(0, remainingDemand);
      item.route.stats.revenue += cabinRevenue;
      uniquePassengers += passengers;
    }
  }

  // 再用剩余座位承接一次中转。乘客同时占用两段座位，任何航段都不会超售。
  const activePairs = new Set([...working.values()].map((item) => pairKey(item.route.originId, item.route.destinationId)));
  const opportunities: Array<{ first: WorkingRoute; second: WorkingRoute; demand: number; fare: number; score: number }> = [];
  for (const hub of next.hubs) {
    const spokes = [...working.values()].filter((item) => item.route.originId === hub.airportId || item.route.destinationId === hub.airportId);
    for (let i = 0; i < spokes.length; i += 1) {
      for (let j = i + 1; j < spokes.length; j += 1) {
        const first = spokes[i];
        const second = spokes[j];
        const firstSpoke = first.route.originId === hub.airportId ? first.route.destinationId : first.route.originId;
        const secondSpoke = second.route.originId === hub.airportId ? second.route.destinationId : second.route.originId;
        if (firstSpoke === secondSpoke || activePairs.has(pairKey(firstSpoke, secondSpoke))) continue;
        const a = airportById(firstSpoke);
        const b = airportById(secondSpoke);
        const directDistance = distanceKm(a, b);
        const itineraryDistance = first.distance + second.distance;
        const detour = itineraryDistance / Math.max(1, directDistance);
        if (detour > 1.65) continue;
        const maturity = Math.min(maturityFactor(first.route.maturityWeeks, first.route.openedWeek), maturityFactor(second.route.maturityWeeks, second.route.openedWeek));
        const hubMultiplier = hubRouteMultiplier(hub);
        const demand = Math.round(marketDemand(a, b, next) * routeDemandMultiplier(undefined, a, b, next) * 0.24 * maturity * (2 - detour) * hubMultiplier.transfer);
        const fare = baseFare(itineraryDistance, 'economy') * 0.9;
        opportunities.push({ first, second, demand, fare, score: fare / detour });
      }
    }
  }
  opportunities.sort((a, b) => b.score - a.score || a.first.route.id.localeCompare(b.first.route.id));

  for (const opportunity of opportunities) {
    let remainingDemand = opportunity.demand;
    let totalPassengers = 0;
    for (const firstWork of opportunity.first.aircraftWork) {
      if (firstWork.remainingSeatsByCabin.economy <= 0 || remainingDemand <= 0) continue;
      for (const secondWork of opportunity.second.aircraftWork) {
        if (secondWork.remainingSeatsByCabin.economy <= 0 || remainingDemand <= 0) continue;
        const passengers = Math.min(remainingDemand, firstWork.remainingSeatsByCabin.economy, secondWork.remainingSeatsByCabin.economy);
        if (passengers <= 0) continue;
        firstWork.remainingSeatsByCabin.economy -= passengers;
        secondWork.remainingSeatsByCabin.economy -= passengers;
        opportunity.first.remainingSeatsByCabin.economy -= passengers;
        opportunity.second.remainingSeatsByCabin.economy -= passengers;
        remainingDemand -= passengers;
        totalPassengers += passengers;
      }
    }
    if (!totalPassengers) continue;
    const totalDistance = opportunity.first.distance + opportunity.second.distance;
    for (const leg of [opportunity.first, opportunity.second]) {
      const origin = airportById(leg.route.originId);
      const destination = airportById(leg.route.destinationId);
      const hubMultiplier = hubRouteMultiplier(hubForRoute(next, leg.route));
      const averageFareMultiplier = leg.aircraftWork.length
        ? leg.aircraftWork.reduce((sum, work) => sum + aircraftFareMultiplier(work.asset, leg.route), 0) / leg.aircraftWork.length
        : routeFareMultiplier(leg.route);
      const revenue = totalPassengers * opportunity.fare * (leg.distance / totalDistance) * routeRevenueMultiplier(leg.route, origin, destination, next) * averageFareMultiplier * hubMultiplier.revenue;
      leg.route.stats.transferPassengers += totalPassengers;
      leg.route.stats.passengers += totalPassengers;
      leg.route.stats.revenue += revenue;
      leg.route.stats.networkContribution += revenue;
      const economyStats = leg.route.stats.cabins.economy;
      economyStats.passengers += totalPassengers;
      economyStats.loadFactor = economyStats.seats > 0 ? Math.min(1, economyStats.passengers / economyStats.seats) : 0;
    }
    uniquePassengers += totalPassengers;
    connectionPassengers += totalPassengers;
  }

  let revenue = 0;
  let operatingCosts = 0;
  const operatingCostBreakdown = {
    fuel: 0,
    landing: 0,
    passenger: 0,
    admin: 0,
    lease: 0,
    assistantSalary: 0,
  };
  let seats = 0;
  let segmentPassengers = 0;
  let unmetDemand = 0;

  const totalFuelGallons = [...working.values()].reduce((sum, item) => sum + item.fuelGallons, 0);
  const fuelResult = settleFuelWeek(next, totalFuelGallons, fuelEffectMultiplier);
  next = fuelResult.state;
  // settleFuelWeek 会克隆 state，需要把 working 里的 route 引用重新绑定到新的 next.routes 上。
  const routeById = new Map(next.routes.map((route) => [route.id, route]));
  for (const item of working.values()) {
    const freshRoute = routeById.get(item.route.id);
    if (freshRoute) item.route = freshRoute;
  }
  // 机组愤怒离职/罢工后，新机组次周到位。
  for (const aircraftId of groundedAircraftIds) {
    const asset = next.aircraft.find((candidate) => candidate.id === aircraftId);
    if (asset) {
      asset.crewMood = 60;
      asset.crewHiredWeek = next.week;
    }
  }
  const originalTotalFlightCost = [...working.values()].reduce((sum, item) => sum + item.flightCost, 0);

  for (const item of working.values()) {
    const hubMultiplier = hubRouteMultiplier(hubForRoute(next, item.route));
    const passengerCost = item.route.aircraftIds.reduce((sum, aircraftId) => {
      const asset = next.aircraft.find((candidate) => candidate.id === aircraftId);
      return sum + (asset ? aircraftType(asset.typeId).perPassengerCost * asset.crewSalaryModifier : 0);
    }, 0) / Math.max(1, item.route.aircraftIds.length) * item.route.stats.passengers * hubMultiplier.passengerCost;
    const fuelShare = originalTotalFlightCost > 0 ? item.flightCost / originalTotalFlightCost : 0;
    const actualFuelCost = fuelResult.cashCost * fuelShare;
    item.route.stats.cashCost = actualFuelCost + item.ownershipCost + item.cycleCost + item.landingCost + passengerCost + ROUTE_ADMIN_COST;
    item.route.stats.costs = {
      fuel: actualFuelCost,
      ownership: item.ownershipCost,
      cycle: item.cycleCost,
      landing: item.landingCost,
      passenger: passengerCost,
      admin: ROUTE_ADMIN_COST,
    };
    item.route.stats.operatingProfit = item.route.stats.revenue - item.route.stats.cashCost;
    const ownershipCashCost = item.aircraftWork.reduce((sum, work) => (
      sum + (work.asset.lease ? work.operation.ownershipCost : 0)
    ), 0);
    const routeCashSettlementCost = actualFuelCost + ownershipCashCost + item.landingCost + passengerCost + ROUTE_ADMIN_COST;
    operatingCostBreakdown.fuel += actualFuelCost;
    operatingCostBreakdown.landing += item.landingCost;
    operatingCostBreakdown.passenger += passengerCost;
    operatingCostBreakdown.admin += ROUTE_ADMIN_COST;
    operatingCostBreakdown.lease += ownershipCashCost;
    item.route.stats.loadFactor = item.route.stats.seats > 0
      ? Math.min(1, item.route.stats.passengers / item.route.stats.seats)
      : 0;
    if (item.route.stats.loadFactor > 1) throw new Error('容量约束失效：载客率超过100%');
    item.route.maturityWeeks += 1;
    revenue += item.route.stats.revenue;
    operatingCosts += routeCashSettlementCost;
    seats += item.route.stats.seats;
    segmentPassengers += item.route.stats.passengers;
    unmetDemand += item.route.stats.unmetDemand;
  }

  const assistantSalaries = weeklyAssistantSalaries(next);
  operatingCosts += assistantSalaries;
  operatingCostBreakdown.assistantSalary += assistantSalaries;

  for (const effect of next.activeEffects) {
    if (effect.kind === 'revenueModifier') {
      revenue = Math.round(revenue * effect.multiplier);
    }
  }

  const hubCosts = Math.round(next.hubs.reduce((sum, hub) => sum + hub.weeklyCost, 0) * hubFeeDiscountFactor(next.week));
  const operatingProfit = revenue - operatingCosts - hubCosts;
  let interest = 0;
  let overdueInterest = 0;
  let principalPaid = 0;

  for (const loan of next.loans) {
    if (loan.kind === 'bank') {
      if (loan.weeksRemaining > 0) {
        loan.weeksRemaining -= 1;
        if (loan.weeksRemaining % BANK_LOAN_INTEREST_INTERVAL_WEEKS === 0) {
          interest += bankLoanMonthlyInterest(loan.principalRemaining, loan.annualRate);
        }
      } else {
        loan.weeksRemaining -= 1;
        if (Math.abs(loan.weeksRemaining) % BANK_LOAN_INTEREST_INTERVAL_WEEKS === 0) {
          const penalty = bankLoanMonthlyInterest(loan.principalRemaining, loan.annualRate) * 2.5;
          interest += penalty;
          overdueInterest += penalty;
        }
      }
      continue;
    }

    const weeklyInterest = loan.principalRemaining * loan.annualRate / 52;
    const payment = Math.min(loan.weeklyPayment, loan.principalRemaining + weeklyInterest);
    const principal = Math.min(loan.principalRemaining, Math.max(0, payment - weeklyInterest));
    interest += weeklyInterest;
    principalPaid += principal;
    loan.principalRemaining -= principal;
    loan.weeksRemaining -= 1;
  }
  next.loans = next.loans.filter((loan) => loan.kind === 'bank' || (loan.principalRemaining > 1 && loan.weeksRemaining > 0));

  let depreciation = 0;
  for (const asset of next.aircraft) {
    const weeklyDepreciation = asset.purchasePrice / (20 * 52);
    asset.bookValue = Math.max(asset.purchasePrice * 0.12, asset.bookValue - weeklyDepreciation);
    depreciation += weeklyDepreciation;
    asset.weeksOwned += 1;
    if (asset.routeIds.length) {
      let flightHours = 0;
      let roundTrips = 0;
      for (const routeId of asset.routeIds) {
        const routeWork = working.get(routeId);
        if (!routeWork) continue;
        const aircraftCount = Math.max(1, routeWork.route.aircraftIds.filter((id) => id === asset.id).length);
        const share = aircraftCount / Math.max(1, routeWork.route.aircraftIds.length);
        flightHours += routeWork.totalFlightHours * share;
        roundTrips += routeWork.route.stats.roundTrips * share;
      }
      asset.totalFlightHours += flightHours;
      asset.totalCycles += roundTrips * 2;
      const utilizationWear = flightHours / 180_000;
      const cycleWear = roundTrips * 2 * 0.0001;
      const nextCondition = clamp(asset.condition - 0.00035 - utilizationWear - cycleWear, 0.55, 1);
      asset.condition = nextCondition;
    }
  }

  // 储油库折旧：20 年直线折旧，无残值，寿命归零后失效。
  for (const facility of next.fuelStorage) {
    if (facility.remainingLifeWeeks <= 0) continue;
    const weeklyDepreciation = facility.purchasePrice / facility.totalLifeWeeks;
    facility.bookValue = Math.max(0, facility.bookValue - weeklyDepreciation);
    facility.remainingLifeWeeks -= 1;
    depreciation += weeklyDepreciation;
  }

  const netProfit = operatingProfit - interest - depreciation;
  const netCashFlow = operatingProfit - interest - principalPaid;
  next.cash += netCashFlow;

  // 现金不允许为负：先强制出售飞机与储油库，再自动申请流动资金贷款，仍不足则破产。
  if (next.cash < 0 && !next.gameOver) {
    const aircraftCandidates = [...next.aircraft].sort((a, b) => a.bookValue - b.bookValue);
    while (next.cash < 0 && aircraftCandidates.length > 0) {
      const aircraft = aircraftCandidates.shift()!;
      sellOneAircraft(next, aircraft);
    }
  }
  if (next.cash < 0 && !next.gameOver) {
    const storageCandidates = [...next.fuelStorage].sort((a, b) => a.bookValue - b.bookValue);
    while (next.cash < 0 && storageCandidates.length > 0) {
      const facility = storageCandidates.shift()!;
      const before = next.cash;
      next = sellOneFuelStorageFacility(next, facility);
      if (next.cash <= before) break;
    }
  }
  if (next.cash < 0 && !next.gameOver) {
    const needed = -next.cash;
    const capacity = bankLoanCapacity(next);
    const borrow = Math.min(capacity, needed);
    if (borrow > 0) {
      const annualRate = bankLoanAnnualRate(next);
      const loanId = `loan-${next.nextId++}`;
      next.cash += borrow;
      next.loans.push({
        id: loanId,
        kind: 'bank',
        originalPrincipal: borrow,
        principalRemaining: borrow,
        annualRate,
        weeklyPayment: bankLoanAverageWeeklyInterest(borrow, annualRate),
        weeksRemaining: BANK_LOAN_TERM_WEEKS,
      });
      addLedger(next, { category: 'debt_draw', cashAmount: borrow, profitAmount: 0, objectId: loanId, label: '破产保护：自动流动资金贷款' });
    }
  }
  if (next.cash < 0 && !next.gameOver) {
    const deficit = -next.cash;
    next.gameOver = {
      acquiredFor: Math.max(0, deficit),
      message: `公司现金枯竭，全部飞机已强制出售且银行授信额度用尽。最终公司以 $${deficit.toLocaleString('zh-CN')} 被收购。`,
    };
    next.cash = 0;
  }

  next.reputation = clamp(next.reputation + (seats > 0 && segmentPassengers / seats > 0.72 ? 0.08 : -0.02), 30, 95);

  const reportWithoutSignals = {
    week: next.week,
    revenue,
    operatingCosts,
    operatingCostBreakdown,
    hubCosts,
    operatingProfit,
    depreciation,
    interest,
    principalPaid,
    netProfit,
    netCashFlow,
    uniquePassengers,
    segmentPassengers,
    seats,
    averageLoadFactor: seats > 0 ? Math.min(1, segmentPassengers / seats) : 0,
    unmetDemand,
    fuelPriceIndex: fuelIndex,
    fuelGallons: totalFuelGallons,
  };
  const report: WeeklyReport = {
    ...reportWithoutSignals,
    signals: buildSignals(reportWithoutSignals, next.lastReport),
  };
  const marketEvent = shouldCreateMarketEvent(next.week) ? createWeeklyMarketEvent(next, `event-${next.nextId++}`) : null;
  next.activeMarketEvents = mergeMarketEvents([
    ...decrementMarketEvents(next.activeMarketEvents),
    ...(marketEvent ? [marketEvent.active] : []),
  ]);
  const allEvents: GameEvent[] = marketEvent ? [marketEvent.event, ...failureEvents] : failureEvents;
  if (allEvents.length) {
    next.events = [...(next.events ?? []), ...allEvents].slice(-36);
    report.signals = [...allEvents.map((item) => ({ id: item.id, tone: item.tone, title: item.title, detail: item.detail })), ...report.signals].slice(0, 3);
  } else {
    next.events ??= [];
  }
  next.lastReport = report;
  next.reports = [...next.reports, report].slice(-104);
  next.careerRecords = {
    ...(next.careerRecords ?? { peakFleetSize: 0, peakActiveRoutes: 0, peakHubCount: 0, totalPassengers: 0, acquiredAirlines: 0 }),
    peakFleetSize: Math.max(next.careerRecords?.peakFleetSize ?? 0, next.aircraft.length),
    peakActiveRoutes: Math.max(next.careerRecords?.peakActiveRoutes ?? 0, next.routes.filter((route) => route.active).length),
    peakHubCount: Math.max(next.careerRecords?.peakHubCount ?? 0, next.hubs.length),
    totalPassengers: (next.careerRecords?.totalPassengers ?? 0) + report.uniquePassengers,
  };

  if (revenue) addLedger(next, { category: 'revenue', cashAmount: revenue, profitAmount: revenue, label: '本周客票与联程收入' });
  addLedger(next, { category: 'operating_expense', cashAmount: -(operatingCosts - assistantSalaries + hubCosts), profitAmount: -(operatingCosts - assistantSalaries + hubCosts), label: '本周航班与枢纽运营成本' });
  if (assistantSalaries) addLedger(next, { category: 'assistant_salary', cashAmount: -assistantSalaries, profitAmount: -assistantSalaries, label: '本周助理薪资' });
  if (interest - overdueInterest) addLedger(next, { category: 'interest', cashAmount: -(interest - overdueInterest), profitAmount: -(interest - overdueInterest), label: '本周贷款利息' });
  if (overdueInterest) addLedger(next, { category: 'interest', cashAmount: -overdueInterest, profitAmount: -overdueInterest, label: '本周逾期贷款罚息' });
  if (principalPaid) addLedger(next, { category: 'principal', cashAmount: -principalPaid, profitAmount: 0, label: '本周偿还贷款本金' });
  next.ledger = next.ledger.slice(-1200);
  next.updatedAt = Date.now();

  // connectionPassengers 被单独保留在计算中，用于确保独立旅客只计一次。
  void connectionPassengers;
  next.listingApplication ??= { active: false, weeksRemaining: 0, suspended: false, auditCompleted: false };
  const settled = next.gameOver ? delistCompany(next, 'player') : next;
  return updateObjectives(decrementEffects(updateSharePrice(processAutomaticDividends(advanceListingApplication(unlockAssistants(settled))), true)));
}
